import { ArrowUpward, ToggleOff, ToggleOn } from "@mui/icons-material";
import { IconButton, styled, Tooltip, Typography } from "@mui/material";
import { useFormikContext } from "formik";
import { uniqBy, map } from "lodash-es";
import { useTranslation } from "react-i18next";
import { FleetConfigFilterButton } from "src/components/FleetConfigFilterButton";
import type { FleetConfig } from "src/types";

export type OrderColumn = "category" | "euro" | "fuel" | "allowed";
export type FilterColumn = "category" | "euro" | "fuel";
export type OrderDirection = "asc" | "desc";
export type Filters = Record<FilterColumn, string[]>;

interface Props {
  orderBy: OrderColumn;
  orderDirection: OrderDirection;
  filters: Filters;
  fleetConfig?: FleetConfig;
  readOnly: boolean;
  filteredData: FleetConfig;
  handleSort: (dataKey: OrderColumn) => void;
  updateFilters: (filterKey: FilterColumn, updatedFilters: Array<string | number>) => void;
}

export function FleetConfigTableHeader(props: Props) {
  const { orderBy, orderDirection, fleetConfig, filters, readOnly, filteredData, updateFilters, handleSort } = props;
  const { t } = useTranslation();
  const { values, setValues } = useFormikContext<FleetConfig>();

  function orderIconClass(category: OrderColumn) {
    if (orderBy === category) {
      return orderDirection;
    }
    return `${orderDirection} hidden`;
  }

  function updateAllowance(allow: boolean) {
    const visibleIds = new Set(filteredData.map((i) => i.id));
    const updatedValues = values.map((item) =>
      visibleIds.has(item.id) && !item.disabled ? { ...item, allowed: allow } : item
    );
    setValues(updatedValues);
  }
  const visibleIds = new Set(filteredData.map((i) => i.id));
  const filteredFormValues = values.filter((item) => visibleIds.has(item.id) && !item.disabled);

  const toggleDirection: "on" | "off" = filteredFormValues.every((item) => item.allowed) ? "on" : "off";

  return (
    <Styles>
      <div className="column" onClick={() => handleSort("category")}>
        <Typography>{t("label.vehicle")}</Typography>
        <FleetConfigFilterButton
          updateFilters={(filters) => updateFilters("category", filters)}
          activeFilters={filters.category}
          title={t("label.filterByVehicle")}
          filters={uniqBy(
            map(fleetConfig, (item) => ({
              id: item.category,
              label: item.categoryTranslation,
            })),
            "id"
          )}
        />
        <IconButton size="small" className={`sort-icon ${orderIconClass("category")}`}>
          <ArrowUpward fontSize="small" />
        </IconButton>
      </div>
      <div className="column" onClick={() => handleSort("euro")}>
        <Typography>{t("label.emission")}</Typography>
        <FleetConfigFilterButton
          updateFilters={(filters) => updateFilters("euro", filters)}
          activeFilters={filters.euro}
          title={t("label.filterByEmission")}
          filters={uniqBy(
            map(fleetConfig, (item) => ({
              id: item.euroTranslation,
              label: item.euroTranslation,
            })),
            "id"
          )}
        />
        <IconButton size="small" className={`sort-icon ${orderIconClass("euro")}`}>
          <ArrowUpward fontSize="small" />
        </IconButton>
      </div>
      <div className="column" onClick={() => handleSort("fuel")}>
        <Typography>{t("label.fuel")}</Typography>
        <FleetConfigFilterButton
          updateFilters={(filters) => updateFilters("fuel", filters)}
          activeFilters={filters.fuel}
          title={t("label.filterByFuel")}
          filters={uniqBy(
            map(fleetConfig, (item) => ({
              id: item.fuel,
              label: item.fuelTranslation,
            })),
            "id"
          )}
        />
        <IconButton size="small" className={`sort-icon ${orderIconClass("fuel")}`}>
          <ArrowUpward fontSize="small" />
        </IconButton>
      </div>
      <div className="column align-right" onClick={() => handleSort("allowed")}>
        <IconButton size="small" className={`sort-icon ${orderIconClass("allowed")}`}>
          <ArrowUpward fontSize="small" />
        </IconButton>
        <Typography>{t("label.allowedInLEZ")}</Typography>
        {!readOnly && (
          <Tooltip title={t("label.toggleAll")}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                updateAllowance(toggleDirection === "off");
              }}
            >
              {toggleDirection === "off" && <ToggleOff fontSize="large" />}
              {toggleDirection === "on" && <ToggleOn fontSize="large" />}
            </IconButton>
          </Tooltip>
        )}
      </div>
    </Styles>
  );
}

const Styles = styled("div")`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  grid-template-rows: 1fr;
  align-items: center;
  width: 100%;
  max-height: 70px;
  height: 100%;
  border-bottom: 1px solid #e0e0e0;
  background-color: #fafafa;
  padding: 0 24px;

  .column {
    height: 100%;
    display: flex;
    flex-direction: row;
    gap: 4px;
    align-items: center;
    transition: opacity 0.3s ease-in-out;
    &:hover {
      cursor: pointer;
      .MuiTypography-root {
        opacity: 0.6;
      }
      .sort-icon {
        opacity: 1;
      }
    }
  }
  .MuiTypography-root {
    font-size: 13px;
    font-weight: 600;
  }
  .align-right {
    justify-content: flex-end;
  }
  .sort-icon {
    transition: transform 0.3s ease-in-out, opacity 0.1s ease-in-out;
    color: ${(props) => props.theme.palette.text.secondary};

    &.hidden {
      opacity: 0;
      svg {
        color: ${(props) => props.theme.palette.text.disabled};
      }
    }
    &.asc {
      transform: rotate(0deg);
    }
    &.desc {
      transform: rotate(180deg);
    }
  }
`;
